import { useRef, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { Button } from '@jeraisonne/ui';
import {
  DOCUMENT_ACCEPT,
  DocumentError,
  type DocumentRow,
  type DocumentTarget,
} from '@jeraisonne/documents';
import { useAuth } from '@/lib/auth';
import { useDeleteDocument, useDocuments, useUploadDocument } from '@/lib/documents/queries';
import { canDeleteDocument, documentMeta } from '@/lib/documents/list-helpers';
import { DocumentItem } from './document-item';

/**
 * Liste + dépôt de fichiers d'UNE cible : fiche d'un contact, classe, réunion
 * passée. Réutilisé tel quel partout (`target` dit où le fichier est rattaché).
 *
 * - `canUpload=false` : lecture seule (ex. un élève dans une classe — seul le
 *   tuteur y dépose ; la RLS le refuse de toute façon).
 * - `isRoomHost` : l'hôte d'une réunion peut supprimer le fichier d'un autre.
 * - Plusieurs fichiers d'un coup, envoyés l'un après l'autre : le premier échec
 *   est affiché, les fichiers déjà envoyés restent.
 *
 * Mobile-first : bouton d'ajout `w-full` sous `sm`, cibles ≥ 44 px.
 */
export function DocumentsPanel({
  target,
  canUpload,
  isRoomHost = false,
  emptyLabel = 'Aucun fichier pour l’instant.',
}: {
  target: DocumentTarget;
  canUpload: boolean;
  isRoomHost?: boolean;
  emptyLabel?: string;
}) {
  const { user } = useAuth();
  const userId = user?.id ?? '';
  const list = useDocuments(target);
  const upload = useUploadDocument();
  const remove = useDeleteDocument();
  const inputRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const picked = Array.from(files);
    setMessage(null);
    for (const file of picked) {
      setProgress(picked.length > 1 ? `Envoi de « ${file.name} »…` : 'Envoi en cours…');
      try {
        await upload.mutateAsync({ file, target });
      } catch (err) {
        setMessage(
          err instanceof DocumentError
            ? err.message
            : "Le fichier n'a pas pu être envoyé. Réessaie.",
        );
        break;
      }
    }
    setProgress(null);
  }

  async function handleDelete(doc: DocumentRow) {
    if (
      !window.confirm(
        `Supprimer « ${doc.fileName} » ? Le fichier sera supprimé pour tout le monde.`,
      )
    )
      return;
    setMessage(null);
    setDeletingId(doc.id);
    try {
      await remove.mutateAsync({ id: doc.id, storagePath: doc.storagePath });
    } catch (err) {
      setMessage(err instanceof DocumentError ? err.message : 'Suppression impossible.');
    } finally {
      setDeletingId(null);
    }
  }

  const docs = list.data ?? [];

  return (
    <div className="space-y-3">
      {canUpload && (
        <div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={DOCUMENT_ACCEPT}
            className="hidden"
            aria-label="Choisir des fichiers à ajouter"
            onChange={(e) => {
              const files = e.target.files;
              void handleFiles(files).finally(() => {
                e.target.value = ''; // permet de re-choisir le même fichier
              });
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
            loading={progress !== null}
            onClick={() => inputRef.current?.click()}
          >
            <Paperclip className="mr-2 size-4" aria-hidden />
            Ajouter un fichier
          </Button>
          <p className="text-ink-400 mt-1.5 text-xs">
            Images, PDF, Word, Excel, PowerPoint — 20 Mo max.
          </p>
        </div>
      )}

      {progress && (
        <p role="status" className="text-ink-500 text-xs">
          {progress}
        </p>
      )}
      {message && (
        <p role="alert" className="text-accent-700 text-xs font-medium">
          {message}
        </p>
      )}

      {list.isLoading && <p className="text-ink-400 text-sm">Chargement des fichiers…</p>}

      {list.isError && (
        <div role="alert" className="text-accent-700 text-sm">
          Impossible de charger les fichiers.{' '}
          <button
            type="button"
            onClick={() => void list.refetch()}
            className="min-h-[2.75rem] font-semibold underline underline-offset-2"
          >
            Réessayer
          </button>
        </div>
      )}

      {!list.isLoading && !list.isError && docs.length === 0 && (
        <div className="border-ink-200 rounded-2xl border bg-white p-5 text-center">
          <p className="text-ink-400 font-sans text-sm">{emptyLabel}</p>
        </div>
      )}

      {docs.length > 0 && (
        <ul className="space-y-2">
          {docs.map((doc) => (
            <DocumentItem
              key={doc.id}
              doc={doc}
              meta={documentMeta(doc, userId)}
              showThumbnail
              canDelete={canDeleteDocument(doc, userId, isRoomHost)}
              deleting={deletingId === doc.id}
              onDelete={(d) => void handleDelete(d)}
              onError={setMessage}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
